import React, { useState, useEffect, useContext, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import axios from "axios";
import { toast } from "react-hot-toast";
import { AuthContext } from "../../context/AuthContext";

const COLORS = {
  Active: "#4ade80",
  Expired: "#fb923c",
  Pending: "#60a5fa",
  Terminated: "#f87171",
  default: "#94a3b8",
};

const CHART_COLORS = [
  "#3b82f6", "#ef4444", "#f59e0b", "#10b981", "#8b5cf6",
  "#6366f1", "#ec4899", "#14b8a6", "#f43f5e", "#84cc16"
];

const formatCurrency = (value) => {
  return `Ksh ${value.toLocaleString()}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white shadow-lg rounded-lg text-xs p-2">
        <p className="font-semibold mb-1">{label}</p>
        <p>
          Count: <span className="font-bold">{payload[0].value}</span>
        </p>
        {payload[0].payload.totalValue && (
          <p>
            Value: <span className="font-bold">{formatCurrency(payload[0].payload.totalValue)}</span>
          </p>
        )}
      </div>
    );
  }
  return null;
};

const ChartSelector = ({ options, selected, onSelect }) => (
  <div className="flex flex-wrap gap-1 mb-2">
    {options.map((option) => (
      <button
        key={option}
        className={`px-2 py-0.5 rounded-full text-xs transition-colors ${
          selected === option
            ? "bg-blue-500 text-white"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        }`}
        onClick={() => onSelect(option)}
      >
        {option}
      </button>
    ))}
  </div>
);

const Chart = () => {
  const [contracts, setContracts] = useState([]);
  const [chartType, setChartType] = useState("Status");
  const [timeRange, setTimeRange] = useState("All Time");
  const [startDate, setStartDate] = useState(new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const { authState } = useContext(AuthContext);

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/bulk/contracts`,
          {
            headers: {
              "x-auth-token": authState.token,
            },
          }
        );
        setContracts(response.data);
      } catch (error) {
        toast.error(error.response?.data?.message || "An unexpected error occurred");
      }
    };

    fetchContracts();
  }, [authState.token]);

  const filteredContracts = useMemo(() => {
    if (timeRange === "Custom Range") {
      return contracts.filter((contract) => {
        const contractDate = new Date(contract.startDate);
        return contractDate >= new Date(startDate) && contractDate <= new Date(endDate);
      });
    }
    if (timeRange === "All Time") return contracts;
    const now = new Date();
    const timeRanges = {
      "Last 30 Days": 30,
      "Last 90 Days": 90,
      "Last 6 Months": 180,
      "Last Year": 365,
    };
    const days = timeRanges[timeRange];
    return contracts.filter((contract) => {
      const contractDate = new Date(contract.startDate);
      return (now - contractDate) / (1000 * 60 * 60 * 24) <= days;
    });
  }, [contracts, timeRange, startDate, endDate]);

  const chartData = useMemo(() => {
    if (chartType === "Status") {
      const statusCounts = filteredContracts.reduce((acc, contract) => {
        acc[contract.status] = (acc[contract.status] || 0) + 1;
        return acc;
      }, {});
      return Object.keys(statusCounts).map((status) => ({
        name: status,
        count: statusCounts[status],
        fill: COLORS[status] || COLORS.default,
      }));
    } else if (chartType === "Category") {
      const categoryData = filteredContracts.reduce((acc, contract) => {
        if (!acc[contract.category]) {
          acc[contract.category] = { count: 0, totalValue: 0 };
        }
        acc[contract.category].count += 1;
        acc[contract.category].totalValue += parseInt(contract.contractAmount || 0);
        return acc;
      }, {});
      return Object.keys(categoryData).map((category, index) => ({
        name: category,
        count: categoryData[category].count,
        totalValue: categoryData[category].totalValue,
        fill: CHART_COLORS[index % CHART_COLORS.length],
      }));
    } else if (chartType === "Monthly Trend") {
      const monthlyData = filteredContracts.reduce((acc, contract) => {
        const date = new Date(contract.startDate);
        const monthYear = `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
        if (!acc[monthYear]) {
          acc[monthYear] = { count: 0, totalValue: 0 };
        }
        acc[monthYear].count += 1;
        acc[monthYear].totalValue += parseInt(contract.contractAmount || 0);
        return acc;
      }, {});
      return Object.keys(monthlyData)
        .sort((a, b) => new Date(a) - new Date(b))
        .map((monthYear) => ({
          name: monthYear,
          count: monthlyData[monthYear].count,
          totalValue: monthlyData[monthYear].totalValue,
        }));
    }
  }, [filteredContracts, chartType]);

  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value;
  };

  const renderChart = () => {
    switch (chartType) {
      case "Status":
        return (
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={formatYAxis} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[2, 2, 0, 0]} animationDuration={500} />
          </BarChart>
        );
      case "Category":
        return (
          <PieChart margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <Pie
              data={chartData}
              dataKey="count"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius="80%"
              fill="#8884d8"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              labelLine={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        );
      case "Monthly Trend":
        return (
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="name" tick={{ fontSize: 8 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={formatYAxis} />
            <Tooltip content={<CustomTooltip />} />
            <Line type="monotone" dataKey="count" stroke="#3b82f6" dot={false} />
            <Line type="monotone" dataKey="totalValue" stroke="#10b981" dot={false} />
          </LineChart>
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full bg-white dark:bg-gray-900 p-3 rounded-lg shadow-lg text-xs">
      <h2 className="text-sm font-bold mb-2 text-gray-800 dark:text-white">Contract Analytics</h2>
      <div className="flex flex-col space-y-1 mb-2">
        <ChartSelector
          options={["Status", "Category", "Monthly Trend"]}
          selected={chartType}
          onSelect={setChartType}
        />
        <ChartSelector
          options={["All Time", "Last 30 Days", "Last 90 Days", "Last 6 Months", "Last Year", "Custom Range"]}
          selected={timeRange}
          onSelect={setTimeRange}
        />
        {timeRange === "Custom Range" && (
          <div className="flex space-x-2 mt-1">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-1 text-xs border rounded"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2 py-1 text-xs border rounded"
            />
          </div>
        )}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={chartType + timeRange + startDate + endDate}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="w-full h-40"
        >
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        </motion.div>
      </AnimatePresence>
      <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
        Contracts: {filteredContracts.length} | 
        Value: {formatCurrency(filteredContracts.reduce((sum, contract) => sum + parseInt(contract.contractAmount || 0), 0))}
      </div>
    </div>
  );
};

export default Chart;